/*!

=========================================================
* Black Dashboard PRO React - v1.2.0
=========================================================

* Product Page: https://www.creative-tim.com/product/black-dashboard-pro-react
* Copyright 2020 Creative Tim (https://www.creative-tim.com)

* Coded by Creative Tim

=========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.

*/

import * as pages from "pages";
import { permission, models, additionalPermission } from "utils/permissionCheck";

export const routes = [
  {
    name: "Home",
    icon: "tim-icons icon-components",
    mini: "H",
    component: pages.MyDashboard,
    path: "/me",
    layout: "",
    role: {
      model: models.ADMIN,
      permissions: [permission.READ_SELF],
    },
  },
  {
    invisible: true,
    name: "Home",
    icon: "tim-icons icon-lock-circle",
    mini: "H",
    component: pages.SchedulePage,
    path: "/",
    layout: "/",
    role: {
      model: models.LESSON_RECORD,
      permissions: [permission.READ_SELF, permission.SCHEDULE_READ],
      check: "or",
    },
  },
  {
    path: "/schedule",
    name: "Schedule",
    icon: "tim-icons icon-calendar-60",
    component: pages.SchedulePage,
    layout: "",
    role: {
      model: models.LESSON_RECORD,
      permissions: [permission.SCHEDULE_READ, permission.READ_SELF],
      check: "or",
    },
  },
  {
    path: "/attendance",
    name: "QR Attendance",
    icon: "tim-icons icon-notes",
    component: pages.ScanAttendancePage,
    layout: "",
    role: {
      model: models.ADMIN,
      permissions: [permission.READ_SELF],
    },
  },
  {
    path: "/availability",
    name: "Availability",
    icon: "tim-icons icon-calendar-60",
    component: pages.AvailabilityPage,
    layout: "",
    role: {
      model: models.ADMIN,
      permissions: [permission.READ_SELF],
    },
  },
  {
    path: "/attendance-application",
    name: "Apply Attendance",
    icon: "tim-icons icon-calendar-60",
    component: pages.AttendanceApplicationPage,
    layout: "",
    role: {
      model: models.ADMIN,
      permissions: [permission.READ_SELF],
    },
  },
  {
    path: "/chat",
    name: "ChatGPT",
    icon: "tim-icons icon-chat-33",
    component: pages.ChatGPTPage,
    layout: "",
    role: {
      model: models.CHATGPT,
      permissions: [permission.READ],
    },
  },
  {
    path: "/topic-frequency",
    name: "Lesson Frequency",
    icon: "tim-icons icon-chat-33",
    component: pages.TopicFrequencyPage,
    layout: "",
    role: {
      model: models.CHATGPT,
      permissions: [permission.READ],
    },
  },
  {
    path: "",
    name: "Course Confidence",
    mini: "CC",
    icon: "tim-icons icon-alert-circle-exc",
    component: pages.CourseConfidencePage,
    layout: "/course-statistics",
    role: {
      model: models.ADMIN,
      permissions: [permission.READ_SELF],
    },
  },
  {
    collapse: true,
    name: "Claims",
    icon: "tim-icons icon-badge",
    state: "claimCollapse",
    views: [
      {
        path: "",
        name: "All Claims",
        mini: "MC",
        component: pages.AllClaimsPage,
        layout: "/claim",
        exact: true,
        role: {
          model: models.CLAIM,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/pending",
        name: "Pending Claims",
        mini: "PC",
        component: pages.PendingClaimsPage,
        layout: "/claim",
        exact: true,
        role: {
          model: models.CLAIM,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/petty-cash",
        name: "Petty Cash Claims",
        mini: "PCC",
        component: pages.PettyCashClaimsPage,
        layout: "/claim",
        exact: true,
        role: {
          model: models.CLAIM,
          permissions: [additionalPermission.CLAIM.PETTY_CASH],
        },
      },

      {
        path: "/self",
        name: "My Claims",
        mini: "MC",
        component: pages.MyClaimsPage,
        layout: "/claim",
        exact: true,
        role: {
          model: models.CLAIM,
          permissions: [permission.READ_SELF],
        },
      },
      {
        path: "/new",
        name: "New Claim",
        mini: "NP",
        component: pages.NewClaimPage,
        layout: "/claim",
        role: {
          model: models.CLAIM,
          permissions: [permission.CREATE],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Member",
    icon: "tim-icons icon-badge",
    state: "pagesCollapse",
    views: [
      {
        path: "",
        name: "All Members",
        mini: "AP",
        component: pages.ParentsPage,
        layout: "/parent",
        exact: true,
        role: {
          model: models.USER,
          permissions: [permission.READ],
        },
      },
      {
        path: "/new",
        name: "New Member",
        mini: "NP",
        component: pages.NewParentPage,
        layout: "/parent",
        role: {
          model: models.USER,
          permissions: [permission.CREATE],
        },
      },
      {
        path: "/points",
        name: "Add Points",
        mini: "P",
        component: pages.AddPointsPage,
        layout: "/parent",
        role: {
          model: models.POINTS,
          permissions: [permission.CREATE],
        },
      },
      {
        invisible: true,
        path: "/edit/:parent_id",
        name: "Edit Member",
        mini: "EM",
        component: pages.EditParentPage,
        layout: "/parent",
        role: {
          model: models.USER,
          permissions: [permission.UPDATE, permission.READ],
        },
      },
      {
        invisible: true,
        path: "/:parent_id",
        name: "View Member",
        mini: "VM",
        component: pages.NewParentPage,
        layout: "/parent",
        role: {
          model: models.USER,
          permissions: [permission.READ],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Staff",
    icon: "tim-icons icon-single-02",
    state: "staffCollapse",
    views: [
      {
        path: "",
        name: "All Staffs",
        mini: "AS",
        component: pages.StaffPage,
        layout: "/staff",
        exact: true,
        role: {
          model: models.ADMIN,
          permissions: [permission.READ],
        },
      },
      {
        path: "/part-time-wage",
        name: "Part Time Wage",
        mini: "PTW",
        component: pages.PartTimeWageReportPage,
        layout: "/staff",
        exact: true,
        role: {
          model: models.REPORT,
          permissions: [additionalPermission.REPORT.PART_TIME_WAGE],
        },
      },
      {
        path: "/new",
        name: "New Staff",
        mini: "NS",
        component: pages.NewStaffPage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.CREATE],
        },
      },
      {
        invisible: true,
        path: "/edit/:staff_id",
        name: "Edit Staff",
        mini: "ES",
        component: pages.EditStaffPage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.UPDATE],
        },
      },
      {
        path: "/reports",
        name: "Reports",
        mini: "R",
        component: pages.StaffReportsPage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/attendance",
        name: "Attendance",
        mini: "A",
        exact: true,
        component: pages.AttendancePage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/attendance/new",
        name: "New Attendance",
        mini: "A",
        component: pages.NewAttendancePage,
        layout: "/staff",
        role: {
          model: models.ATTENDANCE,
          permissions: [permission.ADMIN_EDIT_PERMISSION],
        },
      },
      {
        path: "/attendance/approve",
        name: "Approve Attendance",
        mini: "AA",
        component: pages.ViewAttendanceApplicationPage,
        layout: "/staff",
        role: {
          model: models.ATTENDANCE,
          permissions: [permission.ADMIN_EDIT_PERMISSION],
        },
      },
      {
        path: "/ranking",
        name: "Rankings",
        mini: "TR",
        component: pages.TeachingHourRankingsPage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.READ_ALL],
        },
      },
      {
        invisible: true,
        path: "/attendance/:staff_id",
        name: "Attendance",
        mini: "A",
        component: pages.StaffAttendancePage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.READ_ALL],
        },
      },
      {
        invisible: true,
        path: "/:staff_id",
        name: "View Staff",
        mini: "VS",
        component: pages.ViewStaffPage,
        layout: "/staff",
        role: {
          model: models.ADMIN,
          permissions: [permission.READ],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Leave",
    icon: "tim-icons icon-single-02",
    state: "leaveCollapse",
    views: [
      {
        path: "",
        name: "All Leaves",
        mini: "AL",
        component: pages.AllLeaveApplications,
        layout: "/leave",
        exact: true,
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/me",
        name: "My Leaves",
        mini: "ML",
        component: pages.MyLeaveApplications,
        layout: "/leave",
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.READ],
        },
      },
      {
        path: "/pending",
        name: "Pending Leaves",
        mini: "AL",
        component: pages.PendingLeaveApplicationsPage,
        layout: "/leave",
        exact: true,
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.UPDATE_ALL],
        },
      },
      {
        path: "/new",
        name: "New Leave",
        mini: "NA",
        component: pages.NewLeaveApplicationPage,
        layout: "/leave",
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.CREATE],
        },
      },
      {
        path: "/superadmin-ro",
        name: "Superadmin RO Application",
        mini: "NA",
        component: pages.SudoROApplicationPage,
        layout: "/leave",
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.ADMIN_EDIT_PERMISSION, permission.CREATE],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "OT",
    icon: "tim-icons icon-single-02",
    state: "otCollapse",
    views: [
      {
        path: "",
        name: "All OT",
        mini: "AO",
        component: pages.AllOtApplications,
        layout: "/ot",
        exact: true,
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.READ_ALL],
        },
      },
      {
        path: "/me",
        name: "My OT",
        mini: "MO",
        component: pages.MyOtApplications,
        layout: "/ot",
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.READ],
        },
      },
      {
        path: "/pending",
        name: "Pending OT",
        mini: "PO",
        component: pages.PendingOtApplicationsPage,
        layout: "/ot",
        exact: true,
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.UPDATE_ALL],
        },
      },
      {
        path: "/new",
        name: "New OT",
        mini: "NO",
        component: pages.NewOtApplicationPage,
        layout: "/ot",
        role: {
          model: models.STAFF_APPLICATION,
          permissions: [permission.CREATE],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Accounts",
    icon: "tim-icons icon-money-coins",
    state: "accountsCollapse",
    views: [
      {
        path: "/reports",
        name: "Reports",
        mini: "R",
        component: pages.ReportPage,
        layout: "/accounts",
        exact: true,
        role: {
          model: models.REPORT,
          permissions: [permission.READ],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Courses",
    icon: "tim-icons icon-paper",
    state: "courseCollapse",
    views: [
      {
        path: "/",
        name: "All Courses",
        mini: "AC",
        component: pages.CoursesPage,
        layout: "/course",
        exact: true,
        role: {
          model: models.COURSE,
          permissions: [permission.READ],
        },
      },
      {
        path: "/new",
        name: "New Course",
        mini: "NC",
        component: pages.NewCoursePage,
        layout: "/course",
        role: {
          model: models.COURSE,
          permissions: [permission.CREATE],
        },
      },
      {
        invisible: true,
        path: "/edit/:course_id",
        name: "Edit Course",
        mini: "E",
        component: pages.EditCoursePage,
        layout: "/course",
        role: {
          model: models.COURSE,
          permissions: [permission.UPDATE],
        },
      },
      {
        invisible: true,
        path: "/view/:course_id",
        name: "View Course",
        mini: "E",
        component: pages.ViewCoursePage,
        layout: "/course",
        role: {
          model: models.COURSE,
          permissions: [permission.READ],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Students",
    icon: "tim-icons icon-user-run",
    state: "studentsCollapse",
    views: [
      {
        path: "/",
        name: "All Students",
        mini: "AS",
        component: pages.StudentPage,
        layout: "/student",
        exact: true,
        role: {
          model: models.STUDENT,
          permissions: [permission.READ],
        },
      },
      {
        path: "/new",
        name: "New Student",
        mini: "NS",
        component: pages.NewStudentPage,
        layout: "/student",
        role: {
          model: models.STUDENT,
          permissions: [permission.CREATE],
        },
      },
      {
        path: "/low-hours",
        name: "Low Course Hours",
        mini: "LH",
        component: pages.LowHourCoursePage,
        layout: "/student",
        role: {
          model: models.STUDENT,
          permissions: [permission.READ],
        },
      },
      {
        path: "/add-lesson-record",
        name: "Add Lesson Record",
        mini: "VS",
        component: pages.AddLessonRecord,
        layout: "/student",
        role: {
          model: models.STUDENT,
          permissions: [permission.UPDATE],
        },
      },
      {
        invisible: true,
        path: "/edit/:student_id",
        name: "Edit Student",
        mini: "ES",
        component: pages.EditStudentPage,
        layout: "/student",
        role: {
          model: models.STUDENT,
          permissions: [permission.UPDATE],
        },
      },
      {
        invisible: true,
        path: "/:student_id",
        name: "View Student",
        mini: "VS",
        component: pages.ViewStudentPage,
        layout: "/student",
        role: {
          model: models.STUDENT,
          permissions: [permission.READ],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Lesson Records",
    icon: "tim-icons icon-notes",
    state: "studentReportCollapse",
    views: [
      {
        path: "/",
        name: "All Records",
        mini: "AS",
        component: pages.LessonRecordPage,
        layout: "/student-report",
        exact: true,
        role: {
          model: models.LESSON_RECORD,
          permissions: [permission.ADMIN_EDIT_PERMISSION],
        },
      },
      {
        path: "/pending",
        name: "Pending Records",
        mini: "PR",
        component: pages.PendingStudentRecords,
        layout: "/student-report",
        exact: true,
        role: {
          model: models.LESSON_RECORD,
          permissions: [permission.ADMIN_EDIT_PERMISSION],
        },
      },
      {
        path: "/self",
        name: "My Lesson Records",
        mini: "PR",
        component: pages.MyLessonRecords,
        layout: "/student-report",
        exact: true,
        role: {
          model: models.LESSON_RECORD,
          permissions: [permission.READ_SELF],
        },
      },
    ],
  },
  {
    collapse: true,
    name: "Coffee Shop",
    icon: "tim-icons icon-molecule-40",
    state: "coffeeCollapse",
    views: [
      {
        collapse: true,
        name: "Menu",
        mini: "M",
        state: "coffeeMenuCollapse",
        views: [
          {
            path: "/",
            name: "All Menu Items",
            mini: "B",
            component: pages.MenuItemPage,
            layout: "/coffee/menu-item",
            exact: true,
            role: {
              model: models.COFFEE,
              permissions: [permission.READ],
            },
          },
          {
            path: "/new",
            name: "New Menu Item",
            mini: "B",
            component: pages.NewMenuItemPage,
            layout: "/coffee/menu-item",
            role: {
              model: models.COFFEE,
              permissions: [permission.CREATE],
            },
          },
          {
            invisible: true,
            path: "/edit/:menu_item_id",
            name: "Edit Menu Item",
            mini: "E",
            component: pages.EditMenuItemPage,
            layout: "/coffee/menu-item",
            role: {
              model: models.COFFEE,
              permissions: [permission.UPDATE],
            },
          },
        ],
      },
      {
        collapse: true,
        name: "Points",
        state: "pointsCollapse",
        mini: "P",
        views: [
          {
            path: "/add-points",
            name: "All Records",
            mini: "APR",
            component: pages.AddPointsRecordPage,
            layout: "/coffee",
            exact: true,
            role: {
              model: models.POINTS,
              permissions: [permission.READ],
            },
          },
          {
            invisible: true,
            path: "/add-points/edit/:record_id",
            name: "Add Points Record",
            mini: "APR",
            component: pages.EditAddPointsRecordPage,
            layout: "/coffee",
            role: {
              model: models.POINTS,
              permissions: [permission.UPDATE],
            },
          },
          {
            path: "/add-points/:record_id",
            name: "Add Points Record",
            mini: "APR",
            component: pages.ShowAddPointRecord,
            layout: "/coffee",
            exact: true,
            invisible: true,
            role: {
              model: models.POINTS,
              permissions: [permission.READ],
            },
          },
        ],
      },
      {
        collapse: true,
        name: "Orders",
        state: "ordersCollapse",
        mini: "O",
        views: [
          {
            path: "/view",
            name: "View",
            mini: "CT",
            component: pages.CoffeeTransactionsPage,
            layout: "/coffee",
            role: {
              model: models.ORDER,
              permissions: [permission.READ],
            },
          },
          {
            path: "/report",
            name: "Report",
            mini: "APR",
            component: pages.OrdersReportPage,
            layout: "/coffee",
            exact: true,
            role: {
              model: models.ORDER,
              permissions: [permission.READ],
            },
          },
        ],
      },
    ],
  },
  {
    collapse: true,
    name: "Roles",
    icon: "tim-icons icon-lock-circle",
    state: "rolesCollapse",
    role: {
      model: models.ROLE,
      permissions: [permission.CREATE],
    },
    views: [
      {
        path: "/",
        name: "All Roles",
        mini: "B",
        component: pages.RolesPage,
        layout: "/role",
        exact: true,
        role: {
          model: models.ROLE,
          permissions: [permission.READ],
        },
      },
      {
        path: "/new",
        name: "New Role",
        mini: "B",
        component: pages.NewRolePage,
        layout: "/role",
        role: {
          model: models.ROLE,
          permissions: [permission.CREATE],
        },
      },
      {
        invisible: true,
        path: "/edit/:role_id",
        name: "Edit Role",
        mini: "E",
        component: pages.EditRolePage,
        layout: "/role",
        role: {
          model: models.ROLE,
          permissions: [permission.UPDATE],
        },
      },
    ],
  },
  // {
  // 	invisible: true,
  // 	name: "Profile",
  // 	icon: "tim-icons icon-lock-circle",
  // 	mini: "P",
  // 	component: pages.ProfilePage,
  // 	path: "/",
  // 	layout: "/profile",
  // 	role: {
  // 		model: models.ADMIN,
  // 		permissions: [permission.READ_SELF],
  // 		check: "or",
  // 	},
  // },
  // {
  //     collapse: true,
  //     name: "Forms",
  //     icon: "",
  // },
];
